import { IRxMedicinePrescriptionAllPetDetails, IRxMedicinePrescriptionCreateData } from "@/api_framework/api_modals/EMRs";
import { useUpdateRxMedicinePrescriptionAPI } from "@/app/hooks/api_hooks/orders/useUpdateRxMedicinePrescriptionAPI";
import { useStaticDurationAPI } from "@/app/hooks/api_hooks/emr/useStaticDurationAPI";
import { useStaticFrequencyAPI } from "@/app/hooks/api_hooks/emr/useStaticFrequencyAPI";
import { useStaticTimingsAPI } from "@/app/hooks/api_hooks/emr/useStaticTimingsAPI";
import { useStaticUnitsAPI } from "@/app/hooks/api_hooks/emr/useStaticUnitsAPI";
import { useArrayConstants } from "@/app/hooks/useArrayConstants";
import { useUtils } from "@/app/hooks/useUtils";
import InputWithSuggestionBox from "@/app/components/ui/Inputs/InputWithSuggestionBox";
import {
  createEmptyTaperingDose,
  displayValue,
  IEditableMedicineRow,
  IMedicalSummaryForm,
  IPetDetailsForm,
  ITaperingDoseRow,
} from "@/app/components/routes/auth_routes/OrderManagement/interface";
import { Dialog, Transition } from "@headlessui/react";
import { PlusIcon, TrashIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import ClinicalNotesSection from "./ClinicalNotesSection";
import PetDetailsSection from "./PetDetailsSection";

interface IRxEMRPreviewModalProps {
  open: boolean;
  setOpen: (value: boolean) => void;
  emrData: IRxMedicinePrescriptionCreateData | null;
  orderId?: string;
  userRxKey?: string;
  onStatusUpdated?: (status: "COMPLETED" | "DELETED") => Promise<void> | void;
}

const SuggestionInput = ({
  value,
  onChange,
  placeholder,
  className,
  options,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  className: string;
  options: string[];
}) => (
  <div className={className}>
    <InputWithSuggestionBox
      suggestions={options || []}
      placeholder={placeholder}
      defaultInputValue={value || ""}
      onInputChange={onChange}
      dropdownTextClassName="text-xs"
    />
  </div>
);

const MedicineEditableTable = ({
  title,
  firstColumnLabel,
  listType,
  medicines,
  tableHeaderClass,
  tableCellClass,
  quantityCellClass,
  editableCellInputClass,
  onAddTaperingDose,
  onRemoveTaperingDose,
  onUpdateMedicineRow,
  onUpdateTaperingDose,
  doseUnitOptions,
  frequencyOptions,
  timingOptions,
  durationUnitOptions,
}: {
  title?: string;
  firstColumnLabel: string;
  listType: "added" | "suggestions";
  medicines: IEditableMedicineRow[];
  tableHeaderClass: string;
  tableCellClass: string;
  quantityCellClass: string;
  editableCellInputClass: string;
  onAddTaperingDose: (index: number) => void;
  onRemoveTaperingDose: (rowIndex: number, taperingIndex: number) => void;
  onUpdateMedicineRow: (
    index: number,
    field: "dose" | "dose_unit" | "frequency" | "timing" | "duration" | "duration_unit" | "remark",
    value: string,
  ) => void;
  onUpdateTaperingDose: (
    rowIndex: number,
    taperingIndex: number,
    field: keyof ITaperingDoseRow,
    value: string,
  ) => void;
  doseUnitOptions: string[];
  frequencyOptions: string[];
  timingOptions: string[];
  durationUnitOptions: string[];
}) => {
  if (!medicines?.length) return null;

  return (
    <div className="mb-4 mx-[10px]" data-autosave-section="medicines">
      {title ? <p className="mb-2 px-1 text-sm font-medium tracking-tight42 text-[#000000]">{title}</p> : null}
      <div className="overflow-x-auto rounded-xl bg-[#F5F5F5] p-[3px] pb-[1px]">
        <table className="w-full table-fixed border-separate border-spacing-x-0 border-spacing-y-0.5 rounded-xl bg-[#F5F5F5]">
          <thead className="bg-[#F5F5F5]">
            <tr>
              <th className={`w-[22%] p-3 pl-2 ${tableHeaderClass}`}>{firstColumnLabel}</th>
              <th className={`w-[18%] p-3 pl-2 ${tableHeaderClass}`}>
                Dose <span className="text-[#ED0101]">*</span>
              </th>
              <th className={`w-[12%] p-3 pl-2 ${tableHeaderClass}`}>
                Frequency <span className="text-[#ED0101]">*</span>
              </th>
              <th className={`w-[12%] p-3 pl-2 ${tableHeaderClass}`}>Timing</th>
              <th className={`w-[18%] p-3 pl-2 ${tableHeaderClass}`}>
                Duration <span className="text-[#ED0101]">*</span>
              </th>
              <th className={`w-[12%] p-3 pl-2 ${tableHeaderClass}`}>Remark</th>
              <th className={`w-[6%] p-3 pl-2 ${tableHeaderClass}`}>Quantity</th>
            </tr>
          </thead>
          <tbody className="rounded-xl w-full">
            {medicines.map((med, index) => {
              const isFirst = index === 0;
              const isLast = index === medicines.length - 1;
              const isSingle = medicines.length === 1;

              const firstCellClass = `${tableCellClass} ${
                isSingle ? "rounded-tl-xl rounded-bl-xl" : `${isFirst ? "rounded-tl-xl" : ""} ${isLast ? "rounded-bl-xl" : ""}`
              }`;
              const lastCellClass = `${tableCellClass} ${
                isSingle ? "rounded-tr-xl rounded-br-xl" : `${isFirst ? "rounded-tr-xl" : ""} ${isLast ? "rounded-br-xl" : ""}`
              }`;

              return (
                <Fragment key={index}>
                  <tr className="bg-white">
                    <td className={firstCellClass}>
                      <div className="flex items-center justify-between gap-2">
                        <span>{displayValue(med?.item_name ?? med?.name)}</span>
                        <button
                          type="button"
                          onClick={() => onAddTaperingDose(index)}
                          className="bg-gray-200 p-1 rounded-md text-gray-600 font-bold flex justify-center items-center"
                          title="Add tapering dose"
                        >
                          <PlusIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                    <td className={tableCellClass}>
                      <div className="flex items-center gap-1">
                        <input
                          value={med?.dose ?? ""}
                          onChange={(e) => onUpdateMedicineRow(index, "dose", e.target.value)}
                          className="h-8 w-1/2 min-w-0 rounded-md border border-[#E2E2E2] bg-white px-2 text-xs font-medium text-[#1F1F23] placeholder:text-gray-400 focus:border-[#E2E2E2] focus:ring-1 focus:ring-[#E2E2E2] outline-none"
                          placeholder="e.g 10"
                        />
                        <SuggestionInput
                          value={med?.dose_unit ?? ""}
                          onChange={(value) => onUpdateMedicineRow(index, "dose_unit", value)}
                          className="w-1/2 min-w-0"
                          placeholder="Dose Unit"
                          options={doseUnitOptions}
                        />
                      </div>
                    </td>
                    <td className={tableCellClass}>
                      <SuggestionInput
                        value={med?.frequency ?? ""}
                        onChange={(value) => onUpdateMedicineRow(index, "frequency", value)}
                        className="w-full"
                        placeholder="Frequency"
                        options={frequencyOptions}
                      />
                    </td>
                    <td className={tableCellClass}>
                      <SuggestionInput
                        value={med?.timing ?? ""}
                        onChange={(value) => onUpdateMedicineRow(index, "timing", value)}
                        className="w-full"
                        placeholder="Timing"
                        options={timingOptions}
                      />
                    </td>
                    <td className={tableCellClass}>
                      <div className="flex items-center gap-1">
                        <input
                          value={med?.duration ?? ""}
                          onChange={(e) => onUpdateMedicineRow(index, "duration", e.target.value)}
                          className="h-8 w-1/2 min-w-0 rounded-md border border-[#E2E2E2] bg-white px-2 text-xs font-medium text-[#1F1F23] placeholder:text-gray-400 focus:border-[#E2E2E2] focus:ring-1 focus:ring-[#E2E2E2] outline-none"
                          placeholder="e.g 5"
                        />
                        <SuggestionInput
                          value={med?.duration_unit ?? ""}
                          onChange={(value) => onUpdateMedicineRow(index, "duration_unit", value)}
                          className="w-1/2 min-w-0"
                          placeholder="Duration Unit"
                          options={durationUnitOptions}
                        />
                      </div>
                    </td>
                    <td className={tableCellClass}>
                      <input
                        value={(med?.remark ?? med?.remarks ?? "") as string}
                        onChange={(e) => onUpdateMedicineRow(index, "remark", e.target.value)}
                        className={editableCellInputClass}
                        placeholder="Remarks"
                      />
                    </td>
                    <td className={`${lastCellClass} text-center`}>{displayValue(med?.quantity)}</td>
                  </tr>

                  {(med?.tapering_doses || []).map((dose, taperingIndex) => (
                    <tr key={`${listType}-${index}-taper-${taperingIndex}`} className="bg-[#FCFCFC]">
                      <td className={tableCellClass}>
                        <div className={`flex items-center ${listType === "added" ? "justify-end" : "justify-between"}`}>
                          <span className={listType === "added" ? "pr-4 text-xs text-amber-600 font-medium" : "pl-4 text-[11px] text-amber-600 font-medium"}>
                            Tapering Dosage {taperingIndex + 1}
                          </span>
                          <button
                            type="button"
                            onClick={() => onRemoveTaperingDose(index, taperingIndex)}
                            className="w-8 h-8 flex items-center justify-center bg-[#F2F2F2] rounded-[10px]"
                          >
                            <TrashIcon className="size-5 text-[#ED0101]" />
                          </button>
                        </div>
                      </td>
                      <td className={tableCellClass}>
                        <div className="flex items-center gap-1">
                          <input
                            value={dose?.dose ?? ""}
                            onChange={(e) => onUpdateTaperingDose(index, taperingIndex, "dose", e.target.value)}
                            className="h-8 w-1/2 min-w-0 rounded-md border border-[#E2E2E2] bg-white px-2 text-xs font-medium text-[#1F1F23] placeholder:text-gray-400 focus:border-[#E2E2E2] focus:ring-1 focus:ring-[#E2E2E2] outline-none"
                            placeholder="e.g 10"
                          />
                          <SuggestionInput
                            value={dose?.dose_unit ?? ""}
                            onChange={(value) => onUpdateTaperingDose(index, taperingIndex, "dose_unit", value)}
                            className="w-1/2 min-w-0"
                            placeholder="Dose Unit"
                            options={doseUnitOptions}
                          />
                        </div>
                      </td>
                      <td className={tableCellClass}>
                        <SuggestionInput
                          value={dose?.frequency ?? ""}
                          onChange={(value) => onUpdateTaperingDose(index, taperingIndex, "frequency", value)}
                          className="w-full"
                          placeholder="Frequency"
                          options={frequencyOptions}
                        />
                      </td>
                      <td className={tableCellClass}>
                        <SuggestionInput
                          value={dose?.timing ?? ""}
                          onChange={(value) => onUpdateTaperingDose(index, taperingIndex, "timing", value)}
                          className="w-full"
                          placeholder="Timing"
                          options={timingOptions}
                        />
                      </td>
                      <td className={tableCellClass}>
                        <div className="flex items-center gap-1">
                          <input
                            value={dose?.duration ?? ""}
                            onChange={(e) => onUpdateTaperingDose(index, taperingIndex, "duration", e.target.value)}
                            className="h-8 w-1/2 min-w-0 rounded-md border border-[#E2E2E2] bg-white px-2 text-xs font-medium text-[#1F1F23] placeholder:text-gray-400 focus:border-[#E2E2E2] focus:ring-1 focus:ring-[#E2E2E2] outline-none"
                            placeholder="e.g 5"
                          />
                          <SuggestionInput
                            value={dose?.duration_unit ?? ""}
                            onChange={(value) => onUpdateTaperingDose(index, taperingIndex, "duration_unit", value)}
                            className="w-1/2 min-w-0"
                            placeholder="Duration Unit"
                            options={durationUnitOptions}
                          />
                        </div>
                      </td>
                      <td className={tableCellClass}>
                        <input
                          value={dose?.remark ?? ""}
                          onChange={(e) => onUpdateTaperingDose(index, taperingIndex, "remark", e.target.value)}
                          className={editableCellInputClass}
                          placeholder="Remarks"
                        />
                      </td>
                      <td className={quantityCellClass}>{dose?.quantity ?? ""}</td>
                    </tr>
                  ))}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
const RxEMRPreviewModal = ({ open, setOpen, emrData, orderId, userRxKey, onStatusUpdated }: IRxEMRPreviewModalProps) => {
  const { convertToValidDateStringFormat, getCurrentDate } = useUtils();
  const { petGenders: genderItems } = useArrayConstants();
  const formHook = useForm<IPetDetailsForm>({ mode: "onChange" });
  const medicalSummaryForm = useForm<IMedicalSummaryForm>({ mode: "onChange" });
  const updateRxMedicinePrescriptionAPI = useUpdateRxMedicinePrescriptionAPI();
  const { data: frequencyData, execute: fetchFrequencies } = useStaticFrequencyAPI();
  const { data: durationData, execute: fetchDurations } = useStaticDurationAPI();
  const { data: unitData, execute: fetchUnits } = useStaticUnitsAPI();
  const { data: timingData, execute: fetchTimings } = useStaticTimingsAPI();
  const [editableMedicinesAdded, setEditableMedicinesAdded] = useState<IEditableMedicineRow[]>([]);
  const [editableMedicinesSuggestions, setEditableMedicinesSuggestions] = useState<IEditableMedicineRow[]>([]);
  const [medicationInstructions, setMedicationInstructions] = useState("");
  const [isFinalActionInProgress, setIsFinalActionInProgress] = useState(false);
  const [showAddPetRequired, setShowAddPetRequired] = useState(false);
  const [isAddingNewPet, setIsAddingNewPet] = useState(false);
  const newPetIdRef = useRef<string>("");
  const speciesOptions = [
    { id: "1", title: "Canine" },
    { id: "2", title: "Feline" },
  ];
  const getNewPetId = useCallback(() => {
    if (newPetIdRef.current) return newPetIdRef.current;
    const generatedId =
      typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : `new-pet-${Date.now()}`;
    newPetIdRef.current = generatedId;
    return generatedId;
  }, []);
  const resolveSpecies = useCallback((specie?: string) => {
    const normalizedSpecie = String(specie || "").toLowerCase().trim();
    if (normalizedSpecie === "1" || normalizedSpecie === "canine") {
      return { id: "1", title: "Canine" };
    }
    if (normalizedSpecie === "2" || normalizedSpecie === "feline") {
      return { id: "2", title: "Feline" };
    }
    return { id: "", title: "" };
  }, []);
  const getSpeciesIdByTitle = useCallback((specie?: string) => {
    return resolveSpecies(specie).id;
  }, [resolveSpecies]);

  const petDetailsMap = useMemo(() => {
    const petsMap = new Map<string, IRxMedicinePrescriptionAllPetDetails>();
    if (emrData?.pet_details?.pet_name) {
      petsMap.set(emrData.pet_details.pet_name, emrData.pet_details as IRxMedicinePrescriptionAllPetDetails);
    }
    (emrData?.all_pets || []).forEach((pet) => {
      if (pet?.pet_name) {
        petsMap.set(pet.pet_name, pet);
      }
    });
    return petsMap;
  }, [emrData?.all_pets, emrData?.pet_details]);

  const petSelectItems = useMemo(
    () =>
      Array.from(petDetailsMap.values()).map((pet) => ({
        id: pet.pet_name,
        title: pet.pet_name,
      })),
    [petDetailsMap],
  );
  const petNameValue = formHook.watch("pet_name");
  const petGenderValue = formHook.watch("pet_gender");
  const petSpeciesValue = formHook.watch("pet_specie");
  const petDobValue = formHook.watch("pet_dob");
  const petTemperatureValue = formHook.watch("pet_tempeture");

  useEffect(() => {
    if (!open) return;
    fetchFrequencies();
    fetchDurations();
    fetchUnits();
    fetchTimings();
  }, [open, fetchFrequencies, fetchDurations, fetchUnits, fetchTimings]);

  const frequencyOptions = useMemo(
    () => (frequencyData || []).map((item) => item?.frequency_name).filter(Boolean),
    [frequencyData],
  );
  const durationUnitOptions = useMemo(
    () => (durationData || []).map((item) => item?.duration_name).filter(Boolean),
    [durationData],
  );
  const doseUnitOptions = useMemo(
    () => (unitData || []).map((item) => item?.dose_unit_name).filter(Boolean),
    [unitData],
  );
  const timingOptions = useMemo(
    () => (timingData || []).map((item) => item?.timing_name).filter(Boolean),
    [timingData],
  );
  const petWeightValue = formHook.watch("pet_weight");
  const concernAndSymptomsValue = medicalSummaryForm.watch("concern_and_symptoms");
  const ongoingMedicationValue = medicalSummaryForm.watch("ongoing_medication");

  const areRequiredFieldsFilled = Boolean(
    String(petNameValue || "").trim() &&
      String(petGenderValue || "").trim() &&
      String(petSpeciesValue || "").trim() &&
      String(petDobValue || "").trim() &&
      String(petWeightValue || "").trim() &&
      String(concernAndSymptomsValue || "").trim() &&
      String(ongoingMedicationValue || "").trim(),
  );
  const areMedicineRequiredFieldsFilled = useMemo(() => {
    const allMedicines = [...(editableMedicinesAdded || []), ...(editableMedicinesSuggestions || [])];
    return allMedicines.every((medicine) => {
      const isDoseFilled = Boolean(String(medicine?.dose || "").trim());
      const isDoseUnitFilled = Boolean(String(medicine?.dose_unit || "").trim());
      const isFrequencyFilled = Boolean(String(medicine?.frequency || "").trim());
      const isDurationFilled = Boolean(String(medicine?.duration || "").trim());
      const isDurationUnitFilled = Boolean(String(medicine?.duration_unit || "").trim());
      if (!isDoseFilled || !isDoseUnitFilled || !isFrequencyFilled || !isDurationFilled || !isDurationUnitFilled) {
        return false;
      }

      const taperingDoses = medicine?.tapering_doses || [];
      return taperingDoses.every((dose) => {
        const isTaperDoseFilled = Boolean(String(dose?.dose || "").trim());
        const isTaperDoseUnitFilled = Boolean(String(dose?.dose_unit || "").trim());
        const isTaperFrequencyFilled = Boolean(String(dose?.frequency || "").trim());
        const isTaperDurationFilled = Boolean(String(dose?.duration || "").trim());
        const isTaperDurationUnitFilled = Boolean(String(dose?.duration_unit || "").trim());
        return (
          isTaperDoseFilled &&
          isTaperDoseUnitFilled &&
          isTaperFrequencyFilled &&
          isTaperDurationFilled &&
          isTaperDurationUnitFilled
        );
      });
    });
  }, [editableMedicinesAdded, editableMedicinesSuggestions]);
  const isTemperatureInvalid = (() => {
    const normalized = String(petTemperatureValue ?? "").trim();
    if (!normalized) return false;
    const temp = Number(normalized);
    return Number.isNaN(temp) || temp < 85 || temp > 115;
  })();
  const isFinishPrescriptionDisabled =
    isFinalActionInProgress || !areRequiredFieldsFilled || !areMedicineRequiredFieldsFilled || isTemperatureInvalid;

  useEffect(() => {
    const petDetails = emrData?.pet_details;
    const isMedicinesArray = Array.isArray(emrData?.medicines);
    const medicinesObject = !isMedicinesArray && emrData?.medicines ? (emrData.medicines as any) : null;
    const medicinesArray = isMedicinesArray ? ((emrData?.medicines as IEditableMedicineRow[]) || []).filter(Boolean) : [];
    const safeMedicinesAdded = isMedicinesArray
      ? medicinesArray.filter((item) => String(item?.prescription_type || "").toLowerCase() !== "suggestion")
      : ((medicinesObject?.medicines_added as IEditableMedicineRow[]) || []).filter(Boolean);
    const safeMedicinesSuggestions = isMedicinesArray
      ? medicinesArray.filter((item) => String(item?.prescription_type || "").toLowerCase() === "suggestion")
      : ((medicinesObject?.medicines_suggestions as IEditableMedicineRow[]) || []).filter(Boolean);

    setIsAddingNewPet(false);
    setShowAddPetRequired(false);
    newPetIdRef.current = "";
    formHook.reset({
      pet_name: petDetails?.pet_name || "",
      pet_gender: petDetails?.pet_gender || "",
      pet_specie: petDetails?.pet_species_type_id || getSpeciesIdByTitle(petDetails?.pet_specie),
      pet_dob: petDetails?.pet_age || "",
      pet_tempeture: petDetails?.pet_temperature || "",
      pet_weight: petDetails?.pet_weight_kg || "",
    });
    medicalSummaryForm.reset({
      concern_and_symptoms: emrData?.concern_and_symptoms || "",
      ongoing_medication:
        (emrData?.ongoing_medication || "").toLowerCase() === "yes"
          ? "Yes"
          : (emrData?.ongoing_medication || "").toLowerCase() === "no"
            ? "No"
            : "",
    });
    setEditableMedicinesAdded(safeMedicinesAdded);
    setEditableMedicinesSuggestions(safeMedicinesSuggestions);
    setMedicationInstructions(medicinesObject?.medicines_instructions || "");

  }, [emrData, formHook, medicalSummaryForm, getSpeciesIdByTitle]);

  const sectionOuterClass = "w-full rounded-[10px] bg-white p-3 mt-3";
  const sectionInnerClass = "w-full rounded-xl bg-[#F9FAFB] p-2";
  const sectionTitleClass = "text-[#656565] text-sm font-medium tracking-tight42 px-2";
  const fieldLabelClass = "text-xs mb-1 text-[#656565]";
  const tableHeaderClass = "text-left text-xs font-medium text-[#1F1F23]";
  const tableCellClass = "p-3 text-xs text-[#1F1F23]";
  const quantityCellClass = "p-3 text-xs text-[#1F1F23] text-center";
  const editableCellInputClass =
    "h-8 w-full rounded-md border border-[#E2E2E2] bg-white px-2 text-xs font-medium text-[#1F1F23] placeholder:text-gray-400 focus:border-[#E2E2E2] focus:ring-1 focus:ring-[#E2E2E2] outline-none";

  const normalizeTaperingDoseRow = useCallback((dose: ITaperingDoseRow = {}) => {
    return {
      dose: dose?.dose ?? "",
      dose_unit: dose?.dose_unit ?? "",
      frequency: dose?.frequency ?? "",
      timing: dose?.timing ?? "",
      duration: dose?.duration ?? "",
      duration_unit: dose?.duration_unit ?? "",
      remark: dose?.remark ?? "",
      quantity: dose?.quantity ?? "",
    };
  }, []);

  const normalizeMedicineRow = useCallback(
    (row: IEditableMedicineRow = {}) => {
      const taperingDoses = Array.isArray(row?.tapering_doses) ? row.tapering_doses : [];
      const medicineId = (row as any)?.medicine_id ?? "";

      return {
        name: row?.name ?? row?.item_name ?? "",
        prescription_type: row?.prescription_type ?? "",
        dose: row?.dose ?? "",
        dose_unit: row?.dose_unit ?? "",
        frequency: row?.frequency ?? "",
        timing: row?.timing ?? "",
        duration: row?.duration ?? "",
        duration_unit: row?.duration_unit ?? "",
        remark: row?.remark ?? row?.remarks ?? "",
        medicine_id: medicineId,
        quantity: row?.quantity ?? "",
        tapering_doses: taperingDoses.map((dose) => normalizeTaperingDoseRow(dose)),
      };
    },
    [normalizeTaperingDoseRow],
  );

  const queueAutoSave = useCallback(() => {
    // Per-field autosave intentionally disabled. Draft changes are buffered locally
    // and submitted in a single payload only on final actions.
  }, []);

  const buildFinalPayload = useCallback(
    (emrStatus: "COMPLETED" | "DELETED") => {
      const resolvedOrderId = String(orderId || (emrData as any)?.order_id || "");
      const clinicId = String((emrData as any)?.clinic_id || emrData?.visited_clinic_id || "");
      const selectedPetName = String(formHook.watch("pet_name") || "").trim();
      const selectedPet = petDetailsMap.get(selectedPetName);
      const selectedSpecies = resolveSpecies(formHook.watch("pet_specie"));
      const fallbackSpecies = resolveSpecies(
        emrData?.pet_details?.pet_species_type_id || emrData?.pet_details?.pet_specie,
      );
      const speciesTypeId = selectedSpecies.id || fallbackSpecies.id;
      const speciesTitle = selectedSpecies.title || fallbackSpecies.title;
      const petDetailsPayload = {
        pet_id: isAddingNewPet ? "new_pet" : selectedPet?.pet_id || emrData?.pet_details?.pet_id || "",
        pet_name: formHook.watch("pet_name") || "",
        pet_gender: formHook.watch("pet_gender") || "",
        pet_specie: speciesTitle || "",
        pet_species_type_id: speciesTypeId || "",
        pet_age: formHook.watch("pet_dob") || "",
        pet_temperature: formHook.watch("pet_tempeture") || "",
        pet_temperature_unit: emrData?.pet_details?.pet_temperature_unit || "F",
        pet_weight_kg: formHook.watch("pet_weight") || "",
      };

      const payload: any = {
        emr_status: emrStatus,
        order_id: resolvedOrderId,
        clinic_id: clinicId,
        user_rx_key: userRxKey,
        concern_and_symptoms: medicalSummaryForm.watch("concern_and_symptoms") || "",
        ongoing_medication: String(medicalSummaryForm.watch("ongoing_medication") || "").toLowerCase() === "yes",
        medicines_instructions: medicationInstructions || "",
        customer_details: {
          customer_id: emrData?.customer_details?.customer_id || "",
          customer_name: emrData?.customer_details?.customer_name ?? null,
        },
        doctor_details: {
          doctor_name: emrData?.doctor_details?.doctor_name || "",
          date: emrData?.doctor_details?.date || "",
          registration_number: emrData?.doctor_details?.registration_number || "",
        },
        pet_details: petDetailsPayload,
        medicines: [
          ...(editableMedicinesAdded ?? []).filter(Boolean).map((row) => normalizeMedicineRow(row)),
          ...(editableMedicinesSuggestions ?? []).filter(Boolean).map((row) => normalizeMedicineRow(row)),
        ],
      };

      return payload;
    },
    [
      formHook,
      orderId,
      userRxKey,
      petDetailsMap,
      resolveSpecies,
      emrData?.emr_id,
      emrData?.pet_details?.pet_id,
      emrData?.pet_details?.pet_specie,
      emrData?.pet_details?.pet_species_type_id,
      emrData?.pet_details?.pet_temperature_unit,
      isAddingNewPet,
      medicalSummaryForm,
      medicationInstructions,
      editableMedicinesAdded,
      editableMedicinesSuggestions,
      getNewPetId,
      normalizeMedicineRow,
    ],
  );

  const handleFinalStatusUpdate = async (emrStatus: "COMPLETED" | "DELETED") => {
    const resolvedOrderId = String(orderId || (emrData as any)?.order_id || "");
    const clinicId = String((emrData as any)?.clinic_id || emrData?.visited_clinic_id || "");
    if (emrStatus === "COMPLETED" && !areRequiredFieldsFilled) {
      toast.error("Please fill all required fields before finishing prescription.");
      return;
    }
    if (emrStatus === "COMPLETED" && !areMedicineRequiredFieldsFilled) {
      toast.error("Please fill dose, dose unit, frequency, duration and duration unit for all medicines before finishing prescription.");
      return;
    }
    if (emrStatus === "COMPLETED" && isTemperatureInvalid) {
      toast.error("Invalid temp range");
      return;
    }
    if (!resolvedOrderId || !clinicId) {
      toast.error("Order or clinic details are missing. Please reopen Generate EMR and try again.");
      return;
    }

    try {
      const payload = buildFinalPayload(emrStatus);
      setIsFinalActionInProgress(true);
      const response = await updateRxMedicinePrescriptionAPI(payload as any);
      if (response?.status) {
        await onStatusUpdated?.(emrStatus);
        setOpen(false);
      }
    } finally {
      setIsFinalActionInProgress(false);
    }
  };

  const handlePetSelection = (petName: string) => {
    setIsAddingNewPet(false);
    setShowAddPetRequired(false);
    newPetIdRef.current = "";
    formHook.setValue("pet_name", petName, { shouldValidate: true });
    const selectedPet = petDetailsMap.get(petName);

    if (selectedPet) {
      formHook.setValue("pet_specie", selectedPet.pet_species_type_id || getSpeciesIdByTitle(selectedPet.pet_specie));
      formHook.setValue("pet_dob", selectedPet.pet_age || "");
      formHook.setValue("pet_tempeture", selectedPet.pet_temperature || "");
      formHook.setValue("pet_weight", selectedPet.pet_weight_kg || "");
      if (selectedPet.pet_gender) {
        formHook.setValue("pet_gender", selectedPet.pet_gender);
      }
    }

    queueAutoSave();
  };

  const handleAddPetClick = () => {
    setIsAddingNewPet(true);
    setShowAddPetRequired(true);
    newPetIdRef.current = getNewPetId();
    formHook.setValue("pet_name", "", { shouldValidate: true });
    formHook.setValue("pet_gender", "", { shouldValidate: true });
    formHook.setValue("pet_specie", "", { shouldValidate: true });
    formHook.setValue("pet_dob", "", { shouldValidate: true });
    formHook.setValue("pet_tempeture", "", { shouldValidate: true });
    formHook.setValue("pet_weight", "", { shouldValidate: true });
    formHook.trigger(["pet_name", "pet_gender", "pet_specie", "pet_dob", "pet_tempeture", "pet_weight"]);
  };

  const updateMedicineRow = (
    type: "added" | "suggestions",
    index: number,
    field: "dose" | "dose_unit" | "frequency" | "timing" | "duration" | "duration_unit" | "remark",
    value: string,
  ) => {
    if (type === "added") {
      setEditableMedicinesAdded((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
      queueAutoSave();
      return;
    }
    setEditableMedicinesSuggestions((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
    queueAutoSave();
  };

  const addTaperingDose = (type: "added" | "suggestions", index: number) => {
    if (type === "added") {
      setEditableMedicinesAdded((prev) =>
        prev.map((row, i) =>
          i === index ? { ...row, tapering_doses: [...(row.tapering_doses || []), createEmptyTaperingDose()] } : row,
        ),
      );
      queueAutoSave();
      return;
    }

    setEditableMedicinesSuggestions((prev) =>
      prev.map((row, i) =>
        i === index ? { ...row, tapering_doses: [...(row.tapering_doses || []), createEmptyTaperingDose()] } : row,
      ),
    );
    queueAutoSave();
  };

  const updateTaperingDose = (
    type: "added" | "suggestions",
    rowIndex: number,
    taperingIndex: number,
    field: keyof ITaperingDoseRow,
    value: string,
  ) => {
    const updateList = (list: IEditableMedicineRow[]) =>
      list.map((row, i) => {
        if (i !== rowIndex) return row;
        const nextTapering = (row.tapering_doses || []).map((dose, j) =>
          j === taperingIndex ? { ...dose, [field]: value } : dose,
        );
        return { ...row, tapering_doses: nextTapering };
      });

    if (type === "added") {
      setEditableMedicinesAdded((prev) => updateList(prev));
      queueAutoSave();
      return;
    }

    setEditableMedicinesSuggestions((prev) => updateList(prev));
    queueAutoSave();
  };

  const removeTaperingDose = (type: "added" | "suggestions", rowIndex: number, taperingIndex: number) => {
    const updateList = (list: IEditableMedicineRow[]) =>
      list.map((row, i) => {
        if (i !== rowIndex) return row;
        return { ...row, tapering_doses: (row.tapering_doses || []).filter((_, j) => j !== taperingIndex) };
      });

    if (type === "added") {
      setEditableMedicinesAdded((prev) => updateList(prev));
      queueAutoSave();
      return;
    }

    setEditableMedicinesSuggestions((prev) => updateList(prev));
    queueAutoSave();
  };

  if (!emrData) return null;

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => {}}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500/75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative w-full max-w-6xl transform overflow-hidden rounded-[10px] bg-[#F4F5F7] shadow-xl transition-all">

                <div className="relative z-20 bg-[#F4F5F7] px-4 pb-0 pt-4">
                  <div className={`${sectionOuterClass} !mt-0`}>
                    <div className="flex items-center justify-between gap-3 text-sm">
                      <span className="font-medium text-blue-600 cursor-pointer hover:underline">
                        {`${displayValue(emrData?.customer_details?.customer_name)} / ${displayValue(
                          String(petNameValue || "").trim() || emrData?.pet_details?.pet_name,
                        )}`}<span className="text-black font-semibold">'EMR</span>
                      </span>
                      <button
                        type="button"
                        className="rounded-md p-1 text-[#6B7280] transition-colors hover:bg-[#F3F4F6] hover:text-[#111827]"
                        aria-label="Close EMR modal"
                        onClick={() => setOpen(false)}
                      >
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                </div>

                <div
                  className="max-h-[70vh] space-y-3 overflow-y-auto px-4 pb-4 pt-0"
                >
                  <PetDetailsSection
                    formHook={formHook}
                    isAddingNewPet={isAddingNewPet}
                    sectionOuterClass={sectionOuterClass}
                    sectionInnerClass={sectionInnerClass}
                    sectionTitleClass={sectionTitleClass}
                    fieldLabelClass={fieldLabelClass}
                    showAddPetRequired={showAddPetRequired}
                    petNameValue={petNameValue}
                    petGenderValue={petGenderValue}
                    petSpeciesValue={petSpeciesValue}
                    petDobValue={petDobValue}
                    petTemperatureValue={petTemperatureValue}
                    petWeightValue={petWeightValue}
                    petSelectItems={petSelectItems}
                    genderItems={genderItems}
                    speciesOptions={speciesOptions}
                    getCurrentDate={getCurrentDate}
                    convertToValidDateStringFormat={convertToValidDateStringFormat}
                    onQueueAutoSave={queueAutoSave}
                    onHandlePetSelection={handlePetSelection}
                    onHandleAddPetClick={handleAddPetClick}
                  />

                  <ClinicalNotesSection
                    sectionOuterClass={sectionOuterClass}
                    medicalSummaryForm={medicalSummaryForm}
                    onQueueAutoSave={queueAutoSave}
                  />

                  <div className={sectionOuterClass}>
                    <p className={sectionTitleClass}>
                      Medical Summary
                    </p>
                    <div className="mt-2">

                    <MedicineEditableTable
                      firstColumnLabel="Medicines Names"
                      listType="added"
                      medicines={editableMedicinesAdded}
                      tableHeaderClass={tableHeaderClass}
                      tableCellClass={tableCellClass}
                      quantityCellClass={quantityCellClass}
                      editableCellInputClass={editableCellInputClass}
                      onAddTaperingDose={(index) => addTaperingDose("added", index)}
                      onRemoveTaperingDose={(rowIndex, taperingIndex) => removeTaperingDose("added", rowIndex, taperingIndex)}
                      onUpdateMedicineRow={(index, field, value) => updateMedicineRow("added", index, field, value)}
                      onUpdateTaperingDose={(rowIndex, taperingIndex, field, value) =>
                        updateTaperingDose("added", rowIndex, taperingIndex, field, value)
                      }
                      doseUnitOptions={doseUnitOptions}
                      frequencyOptions={frequencyOptions}
                      timingOptions={timingOptions}
                      durationUnitOptions={durationUnitOptions}
                    />

                    <MedicineEditableTable
                      title="Medicine Suggestions"
                      firstColumnLabel="Item Names"
                      listType="suggestions"
                      medicines={editableMedicinesSuggestions}
                      tableHeaderClass={tableHeaderClass}
                      tableCellClass={tableCellClass}
                      quantityCellClass={quantityCellClass}
                      editableCellInputClass={editableCellInputClass}
                      onAddTaperingDose={(index) => addTaperingDose("suggestions", index)}
                      onRemoveTaperingDose={(rowIndex, taperingIndex) =>
                        removeTaperingDose("suggestions", rowIndex, taperingIndex)
                      }
                      onUpdateMedicineRow={(index, field, value) =>
                        updateMedicineRow("suggestions", index, field, value)
                      }
                      onUpdateTaperingDose={(rowIndex, taperingIndex, field, value) =>
                        updateTaperingDose("suggestions", rowIndex, taperingIndex, field, value)
                      }
                      doseUnitOptions={doseUnitOptions}
                      frequencyOptions={frequencyOptions}
                      timingOptions={timingOptions}
                      durationUnitOptions={durationUnitOptions}
                    />

                    <div className="rounded-xl bg-white p-[10px] mt-2" data-autosave-section="medicines_instructions">
                      <textarea
                        className="w-full border border-[#E2E2E2] rounded-xl p-3 text-sm text-[#6C727F] focus:outline-none focus:ring-1 focus:ring-[#E2E2E2] focus:border-[#E2E2E2]"
                        placeholder="Additional Medication Instructions"
                        value={medicationInstructions}
                        onChange={(e) => setMedicationInstructions(e.target.value)}
                      ></textarea>
                        </div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-end gap-4 border-t border-[#E2E2E2] bg-white px-6 py-4">
                  <button
                    type="button"
                    onClick={() => handleFinalStatusUpdate("DELETED")}
                    disabled={isFinalActionInProgress}
                    className="rounded-lg border border-[#ED0101] px-5 py-2 text-sm font-medium text-[#ED0101] hover:bg-[#fff5f5] disabled:opacity-60"
                  >
                    Reject
                  </button>
                  <button
                    type="button"
                    onClick={() => handleFinalStatusUpdate("COMPLETED")}
                    disabled={isFinishPrescriptionDisabled}
                    className="rounded-lg bg-[#1F1F23] px-5 py-2 text-sm font-medium text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    Finish Prescription
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default RxEMRPreviewModal;
